import {
  iDayByDayItineraryDay,
  iHolidayQuoteItineraryBlock,
  type ItineraryIconType,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import {
  convertLocalDateToConsistentString,
  createDateForCalendar,
  daysBetweenDateStrings,
} from "@/utils/timezone-date-utils";
import { v4 as uuidv4 } from "uuid";

function normalizeDateString(dateString: string | null | undefined): string | null {
  if (!dateString) return null;
  const date = createDateForCalendar(dateString);
  if (!date) return null;
  return convertLocalDateToConsistentString(date);
}

function addDaysToDateString(dateString: string, days: number): string {
  const date = createDateForCalendar(dateString);
  if (!date) return dateString;
  date.setDate(date.getDate() + days);
  return convertLocalDateToConsistentString(date);
}

export function getItineraryBlockDate(
  block: iHolidayQuoteItineraryBlock
): string | null {
  switch (block.type) {
    case "flight":
      return normalizeDateString(block.flightData?.departureDate);
    case "hotel":
      return normalizeDateString(
        block.accommodationData?.rooms?.[0]?.checkInDate
      );
    case "cruise":
      return normalizeDateString(block.cruiseData?.departureDate);
    case "car":
      return normalizeDateString(block.carHireData?.pickUpDate);
    case "transfer":
      return normalizeDateString(block.transferData?.transferDate);
    case "attraction":
      return normalizeDateString(block.attractionData?.date);
    case "excursion":
      return normalizeDateString(block.excursionData?.date);
    case "rail":
      return normalizeDateString(block.railData?.departureDate);
    default:
      return null;
  }
}

export function getItineraryBlockLabel(
  block: iHolidayQuoteItineraryBlock
): string {
  switch (block.type) {
    case "flight": {
      const from = block.flightData?.departureAirport || "?";
      const to = block.flightData?.arrivalAirport || "?";
      return `Flight ${from} → ${to}`;
    }
    case "hotel":
      return (
        block.accommodationData?.accommodationName || "Accommodation"
      );
    case "cruise":
      return block.cruiseData
        ? `Cruise ${block.cruiseData.departurePort} → ${block.cruiseData.arrivalPort}`
        : "Cruise";
    case "car":
      return block.carHireData?.carType
        ? `Car hire — ${block.carHireData.carType}`
        : "Car hire";
    case "transfer":
      return block.transferData
        ? `Transfer ${block.transferData.pickUpLocation} → ${block.transferData.dropOffLocation}`
        : "Transfer";
    case "attraction":
      return block.attractionData?.attractionName || "Attraction";
    case "excursion":
      return block.excursionData?.excursionName || "Excursion";
    case "rail":
      return block.railData
        ? `Rail ${block.railData.departureStation} → ${block.railData.arrivalStation}`
        : "Rail";
    default:
      return "Itinerary item";
  }
}

function iconFromBlockType(
  type: iHolidayQuoteItineraryBlock["type"]
): ItineraryIconType {
  switch (type) {
    case "flight":
      return "plane";
    case "hotel":
      return "hotel";
    case "cruise":
      return "ship";
    case "car":
      return "car";
    case "transfer":
      return "bus";
    case "attraction":
      return "sightseeing";
    case "excursion":
      return "activity";
    case "rail":
      return "train";
    default:
      return "location";
  }
}

function emptyDay(dayNumber: number, date?: string): iDayByDayItineraryDay {
  return {
    id: uuidv4(),
    dayNumber,
    title: `Day ${dayNumber}`,
    summary: "",
    body: "",
    gallery: [],
    date,
    icon: "location",
    linkedItineraryBlockIds: [],
  };
}

function linkBlockToDay(day: iDayByDayItineraryDay, blockId: string) {
  if (!day.linkedItineraryBlockIds.includes(blockId)) {
    day.linkedItineraryBlockIds.push(blockId);
  }
}

function suggestTitleFromBlocks(
  day: iDayByDayItineraryDay,
  blocksById: Map<string, iHolidayQuoteItineraryBlock>
) {
  if (day.title !== `Day ${day.dayNumber}`) return;

  for (const blockId of day.linkedItineraryBlockIds) {
    const block = blocksById.get(blockId);
    if (!block) continue;
    if (block.type === "hotel" && block.accommodationData?.accommodationName) {
      day.title = block.accommodationData.accommodationName;
      return;
    }
  }

  for (const blockId of day.linkedItineraryBlockIds) {
    const block = blocksById.get(blockId);
    if (!block) continue;
    if (block.type === "flight" && block.flightData?.arrivalAirport) {
      day.title = `Arrive ${block.flightData.arrivalAirport}`;
      return;
    }
  }
}

/**
 * Auto-prefills day cards from quote departure/nights and itinerary block dates.
 * Agents can freely edit afterwards (manual authorship with smart defaults).
 */
export function prefillDayByDayDays(quote?: {
  data?: { departureDate?: string; nights?: number };
  itineraryBlocks?: iHolidayQuoteItineraryBlock[];
} | null): iDayByDayItineraryDay[] {
  const itineraryBlocks = quote?.itineraryBlocks ?? [];
  const blocksById = new Map(
    itineraryBlocks.map((block) => [block.id, block])
  );
  const departureDate = normalizeDateString(quote?.data?.departureDate);
  const nights = quote?.data?.nights ?? 0;

  let days: iDayByDayItineraryDay[] = [];

  if (departureDate && nights > 0) {
    const dayCount = nights + 1;
    days = Array.from({ length: dayCount }, (_, index) =>
      emptyDay(index + 1, addDaysToDateString(departureDate, index))
    );
  } else {
    const uniqueDates = Array.from(
      new Set(
        itineraryBlocks
          .map((block) => getItineraryBlockDate(block))
          .filter((date): date is string => Boolean(date))
      )
    ).sort();

    if (uniqueDates.length > 0) {
      days = uniqueDates.map((date, index) => emptyDay(index + 1, date));
    } else {
      days = [emptyDay(1)];
    }
  }

  const dateToDayIndex = new Map(
    days
      .filter((day) => day.date)
      .map((day, index) => [day.date!, index])
  );

  const resolveDayIndexForDate = (date: string): number | null => {
    if (dateToDayIndex.has(date)) return dateToDayIndex.get(date)!;
    if (!departureDate) return null;
    const offset = daysBetweenDateStrings(departureDate, date);
    if (offset < 0 || offset >= days.length) return null;
    return offset;
  };

  for (const block of itineraryBlocks) {
    if (block.type === "hotel") {
      const checkIn = normalizeDateString(
        block.accommodationData?.rooms?.[0]?.checkInDate
      );
      const checkOut = normalizeDateString(
        block.accommodationData?.rooms?.[0]?.checkOutDate
      );

      if (checkIn && checkOut) {
        const stayNights = Math.max(
          1,
          daysBetweenDateStrings(checkIn, checkOut)
        );
        for (let i = 0; i < stayNights; i++) {
          const stayDate = addDaysToDateString(checkIn, i);
          const dayIndex = resolveDayIndexForDate(stayDate);
          if (dayIndex != null) {
            linkBlockToDay(days[dayIndex], block.id);
          }
        }
        continue;
      }
    }

    const date = getItineraryBlockDate(block);
    if (!date) continue;
    const dayIndex = resolveDayIndexForDate(date);
    if (dayIndex != null) {
      linkBlockToDay(days[dayIndex], block.id);
    }
  }

  for (const day of days) {
    suggestTitleFromBlocks(day, blocksById);
    if (!day.icon || day.icon === "location") {
      const firstLinked = day.linkedItineraryBlockIds
        .map((id) => blocksById.get(id))
        .find((block): block is iHolidayQuoteItineraryBlock => Boolean(block));
      if (firstLinked) {
        day.icon = iconFromBlockType(firstLinked.type);
      }
    }
  }

  return days;
}
